import { supabase } from './supabase';
import {
    Idea,
    Timeline,
    PlotPoint,
    CharacterArc,
    Fragment,
    IdeaCard,
    CardInstance,
    AttachedEntity,
    CardRelationship,
    CardFocus,
    EntityType,
    CardRelationType,
    IdeaBoardItem,
    IdeaBeatLink
} from '../types';
import type { StructureTemplate } from './structureTemplates';

export type BoardGeometry = Pick<IdeaBoardItem, 'x' | 'y' | 'width' | 'height'> & { z_index?: number };

// Postgres numeric columns arrive as strings ("39.000"); page math needs numbers
const withNumericPages = (instance: CardInstance): CardInstance => ({
    ...instance,
    page_goal: instance.page_goal == null ? null : Number(instance.page_goal),
    page_length: instance.page_length == null ? null : Number(instance.page_length)
});

// ============================================
// IDEA SERVICE - Core idea operations + Card system
// ============================================

export const IdeaService = {
    // --- Idea CRUD ---

    getIdea: async (id: string): Promise<Idea | null> => {
        const { data, error } = await supabase
            .from('ideas')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            console.error('IdeaService: Get idea error', error);
            return null;
        }
        return data as Idea;
    },

    getIdeaByProjectId: async (projectId: string): Promise<Idea | null> => {
        // Use limit(1) instead of single() to handle cases where multiple ideas exist
        // Order by created_at ascending to get the oldest (first created) idea
        console.log('[IdeaService] getIdeaByProjectId called with:', projectId);
        const { data, error } = await supabase
            .from('ideas')
            .select('*')
            .eq('project_id', projectId)
            .order('created_at', { ascending: true })
            .limit(1);

        console.log('[IdeaService] getIdeaByProjectId result:', { data, error });

        if (error) {
            console.error('IdeaService: Get idea by project error', error);
            return null;
        }

        // data is an array, return first element or null
        const result = (data && data.length > 0) ? data[0] as Idea : null;
        console.log('[IdeaService] getIdeaByProjectId returning:', result?.id);
        return result;
    },

    getIdeaWithRelations: async (id: string): Promise<Idea | null> => {
        const { data, error } = await supabase
            .from('ideas')
            .select(`
                *,
                timelines (
                    *,
                    plot_points (*)
                ),
                character_arcs (*),
                fragments (*)
            `)
            .eq('id', id)
            .single();

        if (error) {
            console.error('IdeaService: Get idea with relations error', error);
            return null;
        }
        return data as Idea;
    },

    createIdea: async (title: string, projectId?: string): Promise<Idea | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('ideas')
            .insert({
                user_id: user.id,
                project_id: projectId,
                title,
                anchor_type: 'statement'
            })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Create idea error', error);
            return null;
        }
        return data as Idea;
    },

    updateIdea: async (id: string, updates: Partial<Idea>): Promise<void> => {
        const { error } = await supabase
            .from('ideas')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', id);

        if (error) {
            console.error('IdeaService: Update idea error', error);
            throw error;
        }
    },

    deleteIdea: async (id: string): Promise<void> => {
        const { error } = await supabase
            .from('ideas')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('IdeaService: Delete idea error', error);
            throw error;
        }
    },

    // --- Timeline CRUD ---

    createTimeline: async (ideaId: string, type: 'macro' | 'mini' | 'spine', title: string): Promise<Timeline | null> => {
        const { data, error } = await supabase
            .from('timelines')
            .insert({
                idea_id: ideaId,
                type,
                title,
                order_index: 0,
                zoom_level: 1
            })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Create timeline error', error);
            return null;
        }
        return data as Timeline;
    },

    // --- Plot Point CRUD ---

    createPlotPoint: async (timelineId: string, title: string, startPos: number, endPos: number): Promise<PlotPoint | null> => {
        const { data, error } = await supabase
            .from('plot_points')
            .insert({
                timeline_id: timelineId,
                title,
                start_position: startPos,
                end_position: endPos,
                color: '#6366f1',
                block_type: 'scene',
                order_index: 0
            })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Create plot point error', error);
            return null;
        }
        return data as PlotPoint;
    },

    updatePlotPoint: async (id: string, updates: Partial<PlotPoint>): Promise<void> => {
        const { error } = await supabase
            .from('plot_points')
            .update(updates)
            .eq('id', id);

        if (error) {
            console.error('IdeaService: Update plot point error', error);
            throw error;
        }
    },

    deletePlotPoint: async (id: string): Promise<void> => {
        const { error } = await supabase
            .from('plot_points')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('IdeaService: Delete plot point error', error);
            throw error;
        }
    },

    // --- Character Arc CRUD ---

    castCharacter: async (ideaId: string, characterId: string, startPos: number, endPos: number): Promise<CharacterArc | null> => {
        const { data, error } = await supabase
            .from('character_arcs')
            .insert({
                idea_id: ideaId,
                character_id: characterId,
                start_position: startPos,
                end_position: endPos,
                arc_type: 'supporting'
            })
            .select(`
                *,
                character:characters_2d_layered(*)
            `)
            .single();

        if (error) {
            console.error('IdeaService: Cast character error', error);
            return null;
        }
        return data as CharacterArc;
    },

    updateCharacterArc: async (id: string, updates: Partial<CharacterArc>): Promise<void> => {
        const { error } = await supabase
            .from('character_arcs')
            .update(updates)
            .eq('id', id);

        if (error) {
            console.error('IdeaService: Update character arc error', error);
            throw error;
        }
    },

    removeCharacterFromIdea: async (id: string): Promise<void> => {
        const { error } = await supabase
            .from('character_arcs')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('IdeaService: Remove character error', error);
            throw error;
        }
    },

    // --- Fragment CRUD ---

    createFragment: async (ideaId: string, title: string, type: 'memory' | 'flashback' | 'note' | 'foreshadow' | 'subplot'): Promise<Fragment | null> => {
        const { data, error } = await supabase
            .from('fragments')
            .insert({
                idea_id: ideaId,
                title,
                type,
                position_x: 100,
                position_y: 100
            })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Create fragment error', error);
            return null;
        }
        return data as Fragment;
    },

    updateFragment: async (id: string, updates: Partial<Fragment>): Promise<void> => {
        const { error } = await supabase
            .from('fragments')
            .update(updates)
            .eq('id', id);

        if (error) {
            console.error('IdeaService: Update fragment error', error);
            throw error;
        }
    },

    deleteFragment: async (id: string): Promise<void> => {
        const { error } = await supabase
            .from('fragments')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('IdeaService: Delete fragment error', error);
            throw error;
        }
    },

    // ============================================
    // INDEX CARD SYSTEM
    // ============================================

    // --- Card CRUD ---

    getCards: async (ideaId: string): Promise<IdeaCard[]> => {
        console.log('[IdeaService] getCards called with ideaId:', ideaId);
        const { data: cards, error } = await supabase
            .from('idea_cards')
            .select('*')
            .eq('idea_id', ideaId)
            .order('created_at', { ascending: true });

        console.log('[IdeaService] getCards result:', { cardCount: cards?.length, error });

        if (error) {
            console.error('IdeaService: Get cards error', error);
            return [];
        }

        // Load entities and instances for each card
        const cardsWithData = await Promise.all(
            (cards || []).map(async (card) => {
                const entities = await IdeaService.getCardEntities(card.id);
                const instances = await IdeaService.getCardInstances(card.id);
                return { ...card, entities, instances } as IdeaCard;
            })
        );

        console.log('[IdeaService] getCards returning', cardsWithData.length, 'cards');
        return cardsWithData;
    },

    getCard: async (cardId: string): Promise<IdeaCard | null> => {
        const { data: card, error } = await supabase
            .from('idea_cards')
            .select('*')
            .eq('id', cardId)
            .single();

        if (error) {
            console.error('IdeaService: Get card error', error);
            return null;
        }

        const entities = await IdeaService.getCardEntities(cardId);
        const instances = await IdeaService.getCardInstances(cardId);
        return { ...card, entities, instances } as IdeaCard;
    },

    createCard: async (
        ideaId: string,
        data: {
            title?: string;
            content?: string;
        } = {}
    ): Promise<IdeaCard | null> => {
        const { data: card, error } = await supabase
            .from('idea_cards')
            .insert({
                idea_id: ideaId,
                title: data.title || '',
                content: data.content || '',
                focus: 'note',
                focus_locked: false
            })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Create card error', error);
            return null;
        }

        return { ...card, entities: [], instances: [] } as IdeaCard;
    },

    updateCard: async (cardId: string, updates: Partial<IdeaCard>): Promise<void> => {
        // Remove entities and instances from updates - they're managed separately
        const { entities, instances, ...cardUpdates } = updates;

        const { error } = await supabase
            .from('idea_cards')
            .update(cardUpdates)
            .eq('id', cardId);

        if (error) {
            console.error('IdeaService: Update card error', error);
            throw error;
        }
    },

    deleteCard: async (cardId: string): Promise<void> => {
        const { error } = await supabase
            .from('idea_cards')
            .delete()
            .eq('id', cardId);

        if (error) {
            console.error('IdeaService: Delete card error', error);
            throw error;
        }
    },

    // --- Card Instances (Timeline Placements) ---

    getCardInstances: async (cardId: string): Promise<CardInstance[]> => {
        const { data, error } = await supabase
            .from('card_instances')
            .select('*')
            .eq('card_id', cardId)
            .order('order_index', { ascending: true });

        if (error) {
            console.error('IdeaService: Get card instances error', error);
            return [];
        }

        return (data as CardInstance[]).map(withNumericPages);
    },

    getInstancesByLane: async (ideaId: string, lane: 'macro' | 'micro'): Promise<CardInstance[]> => {
        // Get all instances for an idea, filtered by lane, with card data
        const { data, error } = await supabase
            .from('card_instances')
            .select(`
                *,
                card:idea_cards!inner(*)
            `)
            .eq('card.idea_id', ideaId)
            .eq('lane', lane)
            .order('order_index', { ascending: true });

        if (error) {
            console.error('IdeaService: Get instances by lane error', error);
            return [];
        }

        return (data as CardInstance[]).map(withNumericPages);
    },

    getAllInstances: async (ideaId: string): Promise<CardInstance[]> => {
        // Get all instances for an idea with card data
        const { data, error } = await supabase
            .from('card_instances')
            .select(`
                *,
                card:idea_cards!inner(*)
            `)
            .eq('card.idea_id', ideaId)
            .order('order_index', { ascending: true });

        if (error) {
            console.error('IdeaService: Get all instances error', error);
            return [];
        }

        return (data as CardInstance[]).map(withNumericPages);
    },

    createInstance: async (
        cardId: string,
        lane: 'macro' | 'micro',
        orderIndex?: number
    ): Promise<CardInstance | null> => {
        // If no order provided, get the next order index for this lane
        let finalOrderIndex = orderIndex;
        if (finalOrderIndex === undefined) {
            const { data: existingInstances } = await supabase
                .from('card_instances')
                .select('order_index')
                .eq('lane', lane)
                .order('order_index', { ascending: false })
                .limit(1);

            finalOrderIndex = (existingInstances && existingInstances[0]?.order_index !== undefined)
                ? existingInstances[0].order_index + 1
                : 0;
        }

        const { data, error } = await supabase
            .from('card_instances')
            .insert({
                card_id: cardId,
                lane,
                order_index: finalOrderIndex
            })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Create instance error', error);
            return null;
        }

        return withNumericPages(data as CardInstance);
    },

    updateInstance: async (instanceId: string, updates: Partial<CardInstance>): Promise<void> => {
        // Remove card from updates - it's joined data
        const { card, ...instanceUpdates } = updates;

        const { error } = await supabase
            .from('card_instances')
            .update(instanceUpdates)
            .eq('id', instanceId);

        if (error) {
            console.error('IdeaService: Update instance error', error);
            throw error;
        }
    },

    deleteInstance: async (instanceId: string): Promise<void> => {
        const { error } = await supabase
            .from('card_instances')
            .delete()
            .eq('id', instanceId);

        if (error) {
            console.error('IdeaService: Delete instance error', error);
            throw error;
        }
    },

    // Move card to a lane (creates instance if unplaced, updates if exists)
    placeCardOnTimeline: async (
        cardId: string,
        lane: 'macro' | 'micro',
        orderIndex?: number
    ): Promise<CardInstance | null> => {
        // Check if card already has an instance
        const existing = await IdeaService.getCardInstances(cardId);

        if (existing.length === 0) {
            // Create new instance at end of lane (or at specified index)
            return IdeaService.createInstance(cardId, lane, orderIndex);
        } else {
            // Update first instance (for simple single-placement mode)
            const updates: Partial<CardInstance> = { lane };
            if (orderIndex !== undefined) {
                updates.order_index = orderIndex;
            }
            await IdeaService.updateInstance(existing[0].id, updates);
            return { ...existing[0], lane, order_index: orderIndex ?? existing[0].order_index };
        }
    },

    // Reorder instances within a lane (batch update order_index)
    reorderInstances: async (
        instanceOrders: Array<{ id: string; order_index: number }>
    ): Promise<void> => {
        // Update each instance's order_index
        const updatePromises = instanceOrders.map(({ id, order_index }) =>
            supabase
                .from('card_instances')
                .update({ order_index })
                .eq('id', id)
        );

        const results = await Promise.all(updatePromises);
        const errors = results.filter(r => r.error);
        if (errors.length > 0) {
            console.error('IdeaService: Reorder instances errors', errors.map(e => e.error));
        }
    },

    // Remove card from timeline (delete all instances)
    removeCardFromTimeline: async (cardId: string): Promise<void> => {
        const { error } = await supabase
            .from('card_instances')
            .delete()
            .eq('card_id', cardId);

        if (error) {
            console.error('IdeaService: Remove card from timeline error', error);
            throw error;
        }
    },

    // --- Card Entities ---

    getCardEntities: async (cardId: string): Promise<AttachedEntity[]> => {
        const { data, error } = await supabase
            .from('card_entities')
            .select('*')
            .eq('card_id', cardId);

        if (error) {
            console.error('IdeaService: Get card entities error', error);
            return [];
        }

        // Resolve entity names based on type
        const entities = await Promise.all(
            (data || []).map(async (e) => {
                let name = 'Unknown';
                let image_url: string | undefined;

                if (e.entity_type === 'subject') {
                    // Get character name
                    const { data: charArc } = await supabase
                        .from('character_arcs')
                        .select('character:characters_2d_layered(name, thumbnail_url)')
                        .eq('id', e.ref_id)
                        .single();
                    if (charArc?.character) {
                        name = (charArc.character as any).name || 'Character';
                        image_url = (charArc.character as any).thumbnail_url;
                    }
                } else if (e.entity_type === 'location') {
                    const { data: loc } = await supabase
                        .from('locations')
                        .select('name, image_url')
                        .eq('id', e.ref_id)
                        .single();
                    if (loc) {
                        name = loc.name;
                        image_url = loc.image_url;
                    }
                } else if (e.entity_type === 'object') {
                    const { data: item } = await supabase
                        .from('items')
                        .select('name, image_url')
                        .eq('id', e.ref_id)
                        .single();
                    if (item) {
                        name = item.name;
                        image_url = item.image_url;
                    }
                }

                return {
                    id: e.id,
                    type: e.entity_type as EntityType,
                    ref_id: e.ref_id,
                    name,
                    snapshot_name: e.snapshot_name,
                    image_url
                } as AttachedEntity;
            })
        );

        return entities;
    },

    addEntityToCard: async (cardId: string, entityType: EntityType, refId: string, snapshotName?: string): Promise<AttachedEntity | null> => {
        // Resolve the entity name for snapshot if not provided
        let resolvedName = snapshotName;
        if (!resolvedName) {
            if (entityType === 'subject') {
                const { data: charArc } = await supabase
                    .from('character_arcs')
                    .select('character:characters_2d_layered(name)')
                    .eq('id', refId)
                    .single();
                resolvedName = (charArc?.character as any)?.name;
            } else if (entityType === 'location') {
                const { data: loc } = await supabase
                    .from('locations')
                    .select('name')
                    .eq('id', refId)
                    .single();
                resolvedName = loc?.name;
            } else if (entityType === 'object') {
                const { data: item } = await supabase
                    .from('items')
                    .select('name')
                    .eq('id', refId)
                    .single();
                resolvedName = item?.name;
            }
        }

        const { data, error } = await supabase
            .from('card_entities')
            .insert({
                card_id: cardId,
                entity_type: entityType,
                ref_id: refId,
                snapshot_name: resolvedName
            })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Add entity to card error', error);
            return null;
        }

        // Return full entity with resolved name
        const entities = await IdeaService.getCardEntities(cardId);
        return entities.find(e => e.id === data.id) || null;
    },

    removeEntityFromCard: async (entityId: string): Promise<void> => {
        const { error } = await supabase
            .from('card_entities')
            .delete()
            .eq('id', entityId);

        if (error) {
            console.error('IdeaService: Remove entity from card error', error);
            throw error;
        }
    },

    // --- Card Relationships ---

    getCardRelationships: async (ideaId: string): Promise<CardRelationship[]> => {
        const { data, error } = await supabase
            .from('card_relationships')
            .select('*')
            .eq('idea_id', ideaId);

        if (error) {
            console.error('IdeaService: Get card relationships error', error);
            return [];
        }

        return data as CardRelationship[];
    },

    createCardRelationship: async (
        ideaId: string,
        fromCardId: string,
        toCardId: string,
        relationType: CardRelationType
    ): Promise<CardRelationship | null> => {
        const { data, error } = await supabase
            .from('card_relationships')
            .insert({
                idea_id: ideaId,
                from_card_id: fromCardId,
                to_card_id: toCardId,
                relation_type: relationType
            })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Create card relationship error', error);
            return null;
        }

        return data as CardRelationship;
    },

    updateCardRelationship: async (relationshipId: string, updates: Partial<CardRelationship>): Promise<void> => {
        const { error } = await supabase
            .from('card_relationships')
            .update(updates)
            .eq('id', relationshipId);

        if (error) {
            console.error('IdeaService: Update card relationship error', error);
            throw error;
        }
    },

    deleteCardRelationship: async (relationshipId: string): Promise<void> => {
        const { error } = await supabase
            .from('card_relationships')
            .delete()
            .eq('id', relationshipId);

        if (error) {
            console.error('IdeaService: Delete card relationship error', error);
            throw error;
        }
    },

    // --- Beat Board (free-canvas geometry + connectors) ---

    getBoardItems: async (ideaId: string): Promise<IdeaBoardItem[]> => {
        const { data, error } = await supabase
            .from('idea_board_items')
            .select('*')
            .eq('idea_id', ideaId)
            .eq('board', 'beat');

        if (error) {
            console.error('IdeaService: Get board items error', error);
            return [];
        }

        return data as IdeaBoardItem[];
    },

    // One row per card per board; inserts on first move, updates after
    saveBoardItem: async (ideaId: string, cardId: string, geometry: BoardGeometry): Promise<IdeaBoardItem | null> => {
        const { data, error } = await supabase
            .from('idea_board_items')
            .upsert(
                { idea_id: ideaId, card_id: cardId, board: 'beat', ...geometry },
                { onConflict: 'card_id,board' }
            )
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Save board item error', error);
            throw error;
        }

        return data as IdeaBoardItem;
    },

    getBeatLinks: async (ideaId: string): Promise<IdeaBeatLink[]> => {
        const { data, error } = await supabase
            .from('idea_beat_links')
            .select('*')
            .eq('idea_id', ideaId);

        if (error) {
            console.error('IdeaService: Get beat links error', error);
            return [];
        }

        return data as IdeaBeatLink[];
    },

    createBeatLink: async (ideaId: string, fromCardId: string, toCardId: string): Promise<IdeaBeatLink | null> => {
        const { data, error } = await supabase
            .from('idea_beat_links')
            .insert({ idea_id: ideaId, from_card_id: fromCardId, to_card_id: toCardId })
            .select()
            .single();

        if (error) {
            console.error('IdeaService: Create beat link error', error);
            return null;
        }

        return data as IdeaBeatLink;
    },

    updateBeatLink: async (linkId: string, updates: Partial<Pick<IdeaBeatLink, 'start_cap' | 'end_cap' | 'color' | 'label' | 'relation_type'>>): Promise<void> => {
        const { error } = await supabase
            .from('idea_beat_links')
            .update(updates)
            .eq('id', linkId);

        if (error) {
            console.error('IdeaService: Update beat link error', error);
            throw error;
        }
    },

    // Adds a structure template's beats as cards, outline appearances and board positions.
    // Acts land in a column on the left of the board, beats beside them, below existing beats.
    applyStructureTemplate: async (
        ideaId: string,
        template: StructureTemplate,
        targetPages: number,
        boardTop: number
    ): Promise<void> => {
        const quarter = (pages: number) => Math.round(pages * 4) / 4;
        const { data: cards, error: cardsError } = await supabase
            .from('idea_cards')
            .insert(template.beats.map(b => ({
                idea_id: ideaId,
                title: b.title,
                content: b.guidance,
                focus: 'note',
                focus_locked: false,
                kind: b.lane === 'macro' ? 'structure' : 'beat',
                color: b.color ?? null
            })))
            .select('id');

        if (cardsError || !cards || cards.length !== template.beats.length) {
            console.error('IdeaService: Apply template cards error', cardsError);
            throw cardsError ?? new Error('Template cards were not created');
        }

        const { error: instancesError } = await supabase
            .from('card_instances')
            .insert(template.beats.map((b, i) => ({
                card_id: cards[i].id,
                lane: b.lane,
                order_index: i,
                page_goal: quarter(targetPages * b.start / 100),
                page_length: Math.max(0.25, quarter(targetPages * b.length / 100))
            })));

        if (instancesError) {
            console.error('IdeaService: Apply template instances error', instancesError);
            throw instancesError;
        }

        let actRow = 0;
        let beatRow = 0;
        const { error: boardError } = await supabase
            .from('idea_board_items')
            .insert(template.beats.map((b, i) => {
                const isAct = b.lane === 'macro';
                const row = isAct ? actRow++ : beatRow++;
                return {
                    idea_id: ideaId,
                    card_id: cards[i].id,
                    board: 'beat',
                    x: isAct ? 40 : 340,
                    y: boardTop + row * 140,
                    width: 260,
                    height: 120,
                    z_index: 0
                };
            }));

        if (boardError) {
            console.error('IdeaService: Apply template board error', boardError);
            throw boardError;
        }
    },

    deleteBeatLink: async (linkId: string): Promise<void> => {
        const { error } = await supabase
            .from('idea_beat_links')
            .delete()
            .eq('id', linkId);

        if (error) {
            console.error('IdeaService: Delete beat link error', error);
            throw error;
        }
    },

    // --- Focus Inference ---

    inferCardFocus: (entities: AttachedEntity[]): CardFocus => {
        const hasLocation = entities.some(e => e.type === 'location');
        const subjects = entities.filter(e => e.type === 'subject');
        const objects = entities.filter(e => e.type === 'object');

        // Priority 1: Any location → Scene
        if (hasLocation) return 'scene';

        // Priority 2: Multiple subjects → Relationship
        if (subjects.length >= 2) return 'relationship';

        // Priority 3: One subject + objects → Prop Significance
        if (subjects.length === 1 && objects.length >= 1) return 'prop_significance';

        // Priority 4: One subject alone → Character
        if (subjects.length === 1) return 'character';

        // Priority 5: Objects only → Prop
        if (objects.length >= 1) return 'prop';

        // Default: Note
        return 'note';
    },

    // Update card focus based on entities (if not locked)
    updateCardFocusFromEntities: async (cardId: string): Promise<CardFocus | null> => {
        const card = await IdeaService.getCard(cardId);
        if (!card || card.focus_locked) return null;

        const newFocus = IdeaService.inferCardFocus(card.entities);
        if (newFocus !== card.focus) {
            await IdeaService.updateCard(cardId, { focus: newFocus });
        }
        return newFocus;
    }
};
